import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AdminSettings,
  AircraftType,
  Booking,
  DailyOpStatus,
  ProductMode,
  Quote,
  Role,
  UserAccount,
} from '../types';
import {
  evaluateCustomRules,
  filterAdvisories,
  getEnforcementPolicy,
  getPlannerAdvisories,
} from '../services/advisoryRules';
import { agentService } from '../services/agent';
import { frmsService, FRMSDuty } from '../services/frms';
import { scheduledSeatService } from '../services/scheduledSeatService';
import {
  plannerService,
  PlannerRoute,
  PlannerRouteSectorInput,
  PlannerRouteStopUpdateInput,
} from '../services/plannerService';
import { inboundInboxService } from '../services/inboundInboxService';
import { PlanningHistoryRow, TacticalMission } from '../components/PlanningBoard/types';
import { usePassengerManifestModal } from './usePassengerManifestModal';
import { buildPlanningHistoryRows } from '../utils/planningHistory';

interface UsePlanningBoardDataParams {
  quotes: Quote[];
  bookings: Booking[];
  aircrafts: AircraftType[];
  currentRole: Role;
  settings: AdminSettings;
  users: UserAccount[];
  initialSelectedDate?: string;
  initialViewMode?: 'TACTICAL' | 'CREW' | 'GROUND' | 'HISTORY' | 'MANIFEST' | 'ROUTE';
  onUpdateQuote?: (quoteId: string, updates: Partial<Quote>) => void;
  onUpdateAircraft?: (aircraftId: string, updates: Partial<AircraftType>) => void;
}
const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
const SCHEDULED_ASSIGNMENTS_STORAGE_KEY = 'ma_planner_scheduled_assignments_by_date_v1';
const SCHEDULED_CREW_STORAGE_KEY = 'ma_planner_scheduled_crew_by_date_v1';
type PlannerSaveStatus = 'idle' | 'saved_to_server' | 'saved_to_local_fallback' | 'failed';

const normalizeDateKey = (value?: string | null): string => {
  if (!value) return '';
  if (value.length >= 10 && value[4] === '-' && value[7] === '-') return value.slice(0, 10);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getUTCFullYear();
  const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
  const day = String(parsed.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const resolveLegDateKey = (quoteDate: string, leg?: { etd?: string | null }): string => {
  const legDate = normalizeDateKey(leg?.etd || '');
  if (legDate) return legDate;
  return normalizeDateKey(quoteDate);
};

const normalizeTailKey = (value?: string | null): string => {
  if (!value) return '';
  const raw = value.trim().toUpperCase();
  if (!raw) return '';
  const firstToken = raw.split(/[\s(/]/)[0] || raw;
  return firstToken.trim();
};

const getTodayLocalDateKey = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isoToClock = (value?: string | null): string => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const hh = String(parsed.getUTCHours()).padStart(2, '0');
  const mm = String(parsed.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const normalizeManifestPassengers = (value: any): Array<{ name: string; nationality?: string; passportNumber?: string; phone?: string }> => {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((p: any) => {
      const full = String(p?.name || p?.fullName || '').trim();
      const composed = [p?.firstName, p?.lastName]
        .map((v) => String(v || '').trim())
        .filter(Boolean)
        .join(' ')
        .trim();
      const name = full || composed || String(p?.passportName || '').trim();
      if (!name) return null;
      return {
        name,
        nationality: String(p?.nationality || '').trim() || undefined,
        passportNumber: String(p?.passportNumber || '').trim() || undefined,
        phone: String(p?.phone || '').trim() || undefined,
      };
    })
    .filter(Boolean) as Array<{ name: string; nationality?: string; passportNumber?: string; phone?: string }>;
};

const resolveDateKeys = (value?: string | null): string[] => {
  const keys = new Set<string>();
  const raw = String(value || '').trim();
  if (!raw) return [];
  if (raw.length >= 10 && raw[4] === '-' && raw[7] === '-') keys.add(raw.slice(0, 10));
  const normalized = normalizeDateKey(raw);
  if (DATE_KEY_RE.test(normalized)) keys.add(normalized);
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    keys.add(parsed.toISOString().slice(0, 10));
    const y = parsed.getUTCFullYear();
    const m = String(parsed.getUTCMonth() + 1).padStart(2, '0');
    const d = String(parsed.getUTCDate()).padStart(2, '0');
    keys.add(`${y}-${m}-${d}`);
  }
  return Array.from(keys).filter((k) => DATE_KEY_RE.test(k));
};

const pickPrimaryDateKey = (value?: string | null): string => {
  const keys = resolveDateKeys(value);
  return keys[0] || '';
};

const readScheduledAssignmentsByDate = (): Record<string, Record<string, string>> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(SCHEDULED_ASSIGNMENTS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, Record<string, string>>;
  } catch {
    return {};
  }
};

const writeScheduledAssignmentsByDate = (value: Record<string, Record<string, string>>): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(SCHEDULED_ASSIGNMENTS_STORAGE_KEY, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

const readScheduledCrewByDate = (): Record<string, Record<string, string[]>> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(SCHEDULED_CREW_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, Record<string, string[]>>;
  } catch {
    return {};
  }
};

const writeScheduledCrewByDate = (value: Record<string, Record<string, string[]>>): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(SCHEDULED_CREW_STORAGE_KEY, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

const getCrewAssignmentGroupSignature = (mission?: TacticalMission | null): string => {
  if (!mission) return '';
  return [
    normalizeDateKey(mission.date),
    normalizeTailKey(mission.tailNumber || ''),
    String(mission.from || '').toUpperCase(),
    String(mission.to || '').toUpperCase(),
    String(mission.etd || ''),
    String(mission.eta || ''),
    String(mission.source || ''),
  ].join('|');
};

const isSyntheticScheduledMission = (mission: TacticalMission): boolean => {
  const bookingId = String(mission.bookingId || '');
  const quoteId = String(mission.quoteId || '');
  return (
    bookingId.startsWith('SPLAN-SOLD-') ||
    bookingId.startsWith('SINV-SOLD-') ||
    quoteId.startsWith('SPLAN-') ||
    quoteId.startsWith('SINV-')
  );
};

export function usePlanningBoardData({
  quotes,
  bookings,
  aircrafts,
  currentRole,
  settings,
  users,
  initialSelectedDate,
  initialViewMode,
  onUpdateQuote,
  onUpdateAircraft,
}: UsePlanningBoardDataParams) {
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const dateKey = normalizeDateKey(initialSelectedDate || '');
    return DATE_KEY_RE.test(dateKey) ? dateKey : getTodayLocalDateKey();
  });
  const [draggedMissionId, setDraggedMissionId] = useState<string | null>(null);
  const [draggedPilotId, setDraggedPilotId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'TACTICAL' | 'CREW' | 'GROUND' | 'HISTORY' | 'MANIFEST' | 'ROUTE'>(initialViewMode || 'TACTICAL');
  const [opStatus] = useState<DailyOpStatus>('OPEN');
  const [showFuelModal, setShowFuelModal] = useState<string | null>(null);
  const [fuelLiters, setFuelLiters] = useState(0);
  const [scheduledAssignments, setScheduledAssignments] = useState<Record<string, string>>({});
  const [scheduledAssignmentsLoadedForDate, setScheduledAssignmentsLoadedForDate] = useState<string>('');
  const [scheduledCrewAssignments, setScheduledCrewAssignments] = useState<Record<string, string[]>>({});
  const [scheduledCrewLoadedForDate, setScheduledCrewLoadedForDate] = useState<string>('');
  const [charterConfirmed, setCharterConfirmed] = useState(false);
  const [scheduledConfirmed, setScheduledConfirmed] = useState(false);
  const [scheduledPersistenceStatus, setScheduledPersistenceStatus] = useState<PlannerSaveStatus>('idle');
  const [crewDutyMap, setCrewDutyMap] = useState<Record<string, { status: 'ON_DUTY' | 'STANDBY' | 'OFF'; duties: FRMSDuty[] }>>({});
  const [crewLoading, setCrewLoading] = useState(false);
  const [crewLegErrors, setCrewLegErrors] = useState<Record<string, string>>({});
  const [scheduledPlanMissions, setScheduledPlanMissions] = useState<TacticalMission[]>([]);
  const [scheduledPlanExternalCountByDate, setScheduledPlanExternalCountByDate] = useState<Record<string, number>>({});
  const [plannerRoutesByTail, setPlannerRoutesByTail] = useState<Record<string, PlannerRoute>>({});
  const [plannerRoutesLoadedDate, setPlannerRoutesLoadedDate] = useState<string>('');
  const [routeSavingTail, setRouteSavingTail] = useState<string | null>(null);
  const [inboundQueueByQuoteId, setInboundQueueByQuoteId] = useState<Record<string, {
    source?: string;
    inboxKey?: string | null;
    subject?: string | null;
    messageId?: string | null;
    threadId?: string | null;
    verificationStatus?: string | null;
    caseState?: string | null;
    repliedAt?: string | null;
  }>>({});
  const [assignmentToast, setAssignmentToast] = useState<string | null>(null);
  const scheduledAssignmentsHydratedRef = useRef(false);
  const scheduledCrewHydratedRef = useRef(false);

  const manifest = usePassengerManifestModal();

  const isDFO = currentRole === 'DFO' || currentRole === 'ADMIN';
  const isFlightPlanner = currentRole === 'FLIGHT_PLANNER' || currentRole === 'ADMIN';

  useEffect(() => {
    const next = normalizeDateKey(initialSelectedDate || '');
    if (!DATE_KEY_RE.test(next)) return;
    setSelectedDate((prev) => (prev === next ? prev : next));
  }, [initialSelectedDate]);

  useEffect(() => {
    if (!initialViewMode) return;
    setViewMode((prev) => (prev === initialViewMode ? prev : initialViewMode));
  }, [initialViewMode]);

  const crewCandidates = useMemo(
    () =>
      Array.isArray(users)
        ? users.filter(u => {
            const role = (u as any).role || u.role;
            return role === 'CP' || role === 'DFO' || role === 'PILOT' || role === 'FIRST_OFFICER' || role === 'FLIGHT_PLANNER';
          })
        : [],
    [users]
  );
  const pilots = crewCandidates;

  const aircraftTailSet = useMemo(
    () => new Set(aircrafts.map(a => normalizeTailKey(a.tailNumber)).filter(Boolean)),
    [aircrafts]
  );

  useEffect(() => {
    setCharterConfirmed(false);
    setScheduledConfirmed(false);
    const dateKey = normalizeDateKey(selectedDate);
    if (!DATE_KEY_RE.test(dateKey)) {
      setScheduledAssignments({});
      setScheduledAssignmentsLoadedForDate('');
      setScheduledCrewAssignments({});
      setScheduledCrewLoadedForDate('');
      return;
    }
    let cancelled = false;
    setScheduledAssignmentsLoadedForDate('');
    scheduledAssignmentsHydratedRef.current = false;
    scheduledCrewHydratedRef.current = false;
    const loadAssignments = async () => {
      let nextAssignments: Record<string, string> | null = null;
      let nextCrewAssignments: Record<string, string[]> | null = null;
      try {
        nextAssignments = await plannerService.getScheduledAssignments(dateKey);
      } catch {
        // fallback to local cache if backend unavailable
      }
      try {
        nextCrewAssignments = await plannerService.getScheduledCrewAssignments(dateKey);
      } catch {
        // fallback to local cache if backend unavailable
      }
      if (!nextAssignments) {
        const byDate = readScheduledAssignmentsByDate();
        nextAssignments = byDate[dateKey] || {};
      }
      if (!nextCrewAssignments) {
        const crewByDate = readScheduledCrewByDate();
        nextCrewAssignments = crewByDate[dateKey] || {};
      }
      if (cancelled) return;
      setScheduledAssignments(nextAssignments || {});
      setScheduledAssignmentsLoadedForDate(dateKey);
      setScheduledCrewAssignments(nextCrewAssignments || {});
      setScheduledCrewLoadedForDate(dateKey);
      setScheduledPersistenceStatus('idle');
    };
    loadAssignments();
    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  useEffect(() => {
    const dateKey = normalizeDateKey(selectedDate);
    if (!DATE_KEY_RE.test(dateKey)) {
      setPlannerRoutesByTail({});
      setPlannerRoutesLoadedDate('');
      return;
    }
    let cancelled = false;
    const loadRoutes = async () => {
      try {
        const routes = await plannerService.getPlannerRoutes(dateKey);
        if (cancelled) return;
        const byTail = (Array.isArray(routes) ? routes : []).reduce((acc, route) => {
          const tail = normalizeTailKey(route?.tailNumber || '');
          if (!tail) return acc;
          acc[tail] = route;
          return acc;
        }, {} as Record<string, PlannerRoute>);
        setPlannerRoutesByTail(byTail);
        setPlannerRoutesLoadedDate(dateKey);
      } catch {
        if (cancelled) return;
        setPlannerRoutesByTail({});
        setPlannerRoutesLoadedDate(dateKey);
      }
    };
    loadRoutes();
    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  useEffect(() => {
    const dateKey = normalizeDateKey(selectedDate);
    if (!DATE_KEY_RE.test(dateKey)) return;
    if (scheduledAssignmentsLoadedForDate !== dateKey) return;
    if (!scheduledAssignmentsHydratedRef.current) {
      scheduledAssignmentsHydratedRef.current = true;
      return;
    }
    const byDate = readScheduledAssignmentsByDate();
    byDate[dateKey] = scheduledAssignments || {};
    const localSaved = writeScheduledAssignmentsByDate(byDate);
    plannerService.saveScheduledAssignments(dateKey, scheduledAssignments || {})
      .then(() => {
        setScheduledPersistenceStatus('saved_to_server');
        setAssignmentToast('Saved to planner');
      })
      .catch(() => {
        if (localSaved) {
          setScheduledPersistenceStatus('saved_to_local_fallback');
          setAssignmentToast('Saved locally only — server unavailable');
          return;
        }
        setScheduledPersistenceStatus('failed');
        setAssignmentToast('Assignment not saved');
      });
  }, [selectedDate, scheduledAssignments, scheduledAssignmentsLoadedForDate]);

  useEffect(() => {
    const dateKey = normalizeDateKey(selectedDate);
    if (!DATE_KEY_RE.test(dateKey)) return;
    if (scheduledCrewLoadedForDate !== dateKey) return;
    if (!scheduledCrewHydratedRef.current) {
      scheduledCrewHydratedRef.current = true;
      return;
    }
    const byDate = readScheduledCrewByDate();
    byDate[dateKey] = scheduledCrewAssignments || {};
    const localSaved = writeScheduledCrewByDate(byDate);
    plannerService.saveScheduledCrewAssignments(dateKey, scheduledCrewAssignments || {})
      .then(() => {
        setScheduledPersistenceStatus('saved_to_server');
        setAssignmentToast('Saved to planner');
      })
      .catch(() => {
        if (localSaved) {
          setScheduledPersistenceStatus('saved_to_local_fallback');
          setAssignmentToast('Saved locally only — server unavailable');
          return;
        }
        setScheduledPersistenceStatus('failed');
        setAssignmentToast('Assignment not saved');
      });
  }, [selectedDate, scheduledCrewAssignments, scheduledCrewLoadedForDate]);

  useEffect(() => {
    let cancelled = false;
    const loadInboundQueue = async () => {
      try {
        const result = await inboundInboxService.getQueue({
          page: 1,
          pageSize: 250,
          includeNonFlight: true,
          includeSpam: true,
          flightOnly: false,
        });
        if (cancelled) return;
        const next = (result.items || []).reduce((acc, item) => {
          const quoteId = String(item.linkedQuoteId || '').trim();
          if (!quoteId) return acc;
          const existing = acc[quoteId];
          const currentTs = new Date(item.receivedAt || item.createdAt || 0).getTime();
          const existingTs = existing ? new Date(existing.repliedAt || 0).getTime() : Number.NEGATIVE_INFINITY;
          if (!existing || currentTs >= existingTs) {
            acc[quoteId] = {
              source: 'EMAIL_INTAKE',
              inboxKey: item.inboxKey || null,
              subject: item.subject || null,
              messageId: item.id,
              threadId: item.threadId,
              verificationStatus: item.verificationStatus || null,
              caseState: item.caseState || null,
              repliedAt: item.repliedAt || null,
            };
          }
          return acc;
        }, {} as Record<string, {
          source?: string;
          inboxKey?: string | null;
          subject?: string | null;
          messageId?: string | null;
          threadId?: string | null;
          verificationStatus?: string | null;
          caseState?: string | null;
          repliedAt?: string | null;
        }>);
        setInboundQueueByQuoteId(next);
      } catch {
        if (!cancelled) setInboundQueueByQuoteId({});
      }
    };
    loadInboundQueue();
    return () => {
      cancelled = true;
    };
  }, []);


  useEffect(() => {
    let cancelled = false;
    const loadScheduledPlanTickets = async () => {
      try {
        const selectedDateKey = normalizeDateKey(selectedDate);
        if (!selectedDateKey) {
          if (!cancelled) setScheduledPlanMissions([]);
          return;
        }
        const selectedDateObj = new Date(`${selectedDateKey}T00:00:00.000Z`);
        const monthStartDate = new Date(Date.UTC(selectedDateObj.getUTCFullYear(), selectedDateObj.getUTCMonth(), 1, 0, 0, 0, 0));
        const monthEndDate = new Date(Date.UTC(selectedDateObj.getUTCFullYear(), selectedDateObj.getUTCMonth() + 1, 1, 0, 0, 0, 0));
        const rangeStart = monthStartDate.toISOString();
        const rangeEnd = monthEndDate.toISOString();
        const localBookingIdsByDate = bookings.reduce((acc, booking) => {
          const dateKey = normalizeDateKey(booking.date);
          if (!DATE_KEY_RE.test(dateKey)) return acc;
          if (!acc[dateKey]) acc[dateKey] = new Set<string>();
          acc[dateKey].add(String(booking.id));
          return acc;
        }, {} as Record<string, Set<string>>);
        const [seatPlanFilteredResult, inventoryRowsResult] = await Promise.allSettled([
          scheduledSeatService.listSeatPlan({
            // backend max is 300
            limit: 300,
            departureFrom: rangeStart,
            departureTo: rangeEnd,
          }),
          scheduledSeatService.listInventory({
            // backend max is 200
            limit: 200,
          }),
        ]);
        const seatPlanFiltered = seatPlanFilteredResult.status === 'fulfilled' ? seatPlanFilteredResult.value : [];
        const inventoryRows = inventoryRowsResult.status === 'fulfilled' ? inventoryRowsResult.value : [];
        let seatPlan = Array.isArray(seatPlanFiltered) ? seatPlanFiltered : [];
        const filteredHasSelectedDate = seatPlan.some((plan: any) =>
          resolveDateKeys(String(plan?.departureAt || '')).includes(selectedDateKey)
        );
        if (!filteredHasSelectedDate) {
          try {
            const broad = await scheduledSeatService.listSeatPlan({ limit: 300 });
            if (Array.isArray(broad) && broad.length > 0) {
              seatPlan = broad;
            }
          } catch {
            // keep filtered data if broad fetch fails
          }
        }
        if (cancelled) return;
        const externalBookingIdsByDate: Record<string, Set<string>> = {};
        const dedupedSeatPlan = (() => {
          const source = Array.isArray(seatPlan) ? seatPlan : [];
          const bySignature = new Map<string, any>();
          const safeIso = (value: any): string => {
            const parsed = new Date(value || 0);
            return Number.isNaN(parsed.getTime()) ? String(value || '') : parsed.toISOString();
          };
          source.forEach((plan: any) => {
            const signature = [
              String(plan?.fromCode || ''),
              String(plan?.toCode || ''),
              safeIso(plan?.departureAt),
              String(plan?.status || ''),
              String(plan?.source || ''),
              Number(plan?.totalSeats || 0),
              Number(plan?.availableSeats || 0),
              Number(plan?.heldSeats || 0),
              Number(plan?.soldSeats || 0),
              Number(plan?.seatPriceUSD || 0),
            ].join('|');
            const existing = bySignature.get(signature);
            if (!existing) {
              bySignature.set(signature, plan);
              return;
            }
            const existingTs = new Date(existing?.departureAt || 0).getTime();
            const planTs = new Date(plan?.departureAt || 0).getTime();
            if (planTs <= existingTs) bySignature.set(signature, plan);
          });
          return Array.from(bySignature.values());
        })();

        const seatPlanInventoryIds = new Set(
          dedupedSeatPlan.map((p: any) => String(p?.id || '')).filter(Boolean)
        );

        const planMissions = dedupedSeatPlan.flatMap((plan: any) => {
          const routeFrom = String((plan as any).fromCode || '').toUpperCase();
          const routeTo = String((plan as any).toCode || '').toUpperCase();
          const departureAt = String((plan as any).departureAt || '');
          const arrivalAt = String((plan as any).arrivalAt || '');
          const dateKeys = resolveDateKeys(departureAt);
          const dateKey = pickPrimaryDateKey(departureAt);
          const normalizedPlanTail = normalizeTailKey(
            String((plan as any)?.metadata?.tailNumber || (plan as any)?.metadata?.aircraftTail || '')
          );
          const fallbackTail =
            normalizedPlanTail && aircraftTailSet.has(normalizedPlanTail) ? normalizedPlanTail : 'UNASSIGNED';
          const bookings = Array.isArray((plan as any).bookings) ? (plan as any).bookings : [];
          const activeBookings = bookings.filter((b: any) => {
            const status = String(b?.status || '').trim().toUpperCase();
            if (['CANCELLED', 'EXPIRED', 'DECLINED', 'REJECTED'].includes(status)) return false;
            // Legacy rows can still carry BOOKED; normalize it to CONFIRMED for planner inclusion.
            const normalizedStatus = status === 'BOOKED' ? 'CONFIRMED' : status;
            return Number(b?.seats || 0) > 0 || normalizedStatus === 'CONFIRMED' || normalizedStatus === 'HELD';
          });

          if (activeBookings.length === 0 && Number((plan as any)?.soldSeats || 0) > 0) {
            const syntheticBookingId = `SPLAN-SOLD-${String((plan as any).id || '')}`;
            if (DATE_KEY_RE.test(dateKey) && !(localBookingIdsByDate[dateKey]?.has(syntheticBookingId))) {
              if (!externalBookingIdsByDate[dateKey]) externalBookingIdsByDate[dateKey] = new Set<string>();
              externalBookingIdsByDate[dateKey].add(syntheticBookingId);
            }
            if (!dateKeys.includes(selectedDateKey)) return [] as TacticalMission[];
            const targetTail = scheduledAssignments[syntheticBookingId] || fallbackTail;
            return [{
              id: `SPLAN-${plan.id}-${syntheticBookingId}`,
              from: routeFrom,
              to: routeTo,
              paxCount: Number((plan as any)?.soldSeats || 0),
              etd: isoToClock(departureAt),
              eta: isoToClock(arrivalAt),
              date: selectedDateKey,
              customerName: String((plan as any)?.title || 'Scheduled Confirmed Seats'),
              quoteId: `SPLAN-${plan.id}`,
              tailNumber: targetTail,
              productMode: 'SCHEDULED' as ProductMode,
              legIndex: 0,
              source: 'SCHEDULED' as const,
              bookingId: syntheticBookingId,
              assignedCrew: scheduledCrewAssignments[syntheticBookingId] || [],
            } as TacticalMission];
          }

          return activeBookings
            .flatMap((b: any, idx: number) => {
              const bookingId = String(b?.bookingId || b?.metadata?.bookingId || b?.id || `PLAN-${plan.id}-${idx}`);
                  if (DATE_KEY_RE.test(dateKey) && !(localBookingIdsByDate[dateKey]?.has(bookingId))) {
                    if (!externalBookingIdsByDate[dateKey]) externalBookingIdsByDate[dateKey] = new Set<string>();
                    externalBookingIdsByDate[dateKey].add(bookingId);
                  }
                  if (!dateKeys.includes(selectedDateKey)) return [] as TacticalMission[];
                  const targetTail = scheduledAssignments[bookingId] || fallbackTail;
                  const bookingMetadata = (b?.metadata || {}) as any;
                  const manifestPassengers = normalizeManifestPassengers(
                    bookingMetadata?.passengers || bookingMetadata?.passengerDetails
                  );
                  return [{
                id: `SPLAN-${plan.id}-${bookingId}`,
                from: routeFrom,
                to: routeTo,
                paxCount: Number(b?.seats || 0),
                etd: isoToClock(departureAt),
                eta: isoToClock(arrivalAt),
                date: selectedDateKey,
                customerName: String(b?.customerName || b?.customerEmail || 'Scheduled Ticket'),
                quoteId: `SPLAN-${plan.id}`,
                tailNumber: targetTail,
                productMode: 'SCHEDULED' as ProductMode,
                legIndex: idx,
                source: 'SCHEDULED' as const,
                bookingId,
                scheduledBookingDbId: String(b?.id || ''),
                assignedCrew: scheduledCrewAssignments[bookingId] || [],
                manifestPassengers,
              } as TacticalMission];
            });
        });
        const inventoryFallbackMissions = (Array.isArray(inventoryRows) ? inventoryRows : []).flatMap((inv: any) => {
          const invId = String(inv?.id || '');
          if (invId && seatPlanInventoryIds.has(invId)) return [] as TacticalMission[];
          const departureAt = String(inv?.departureAt || '');
          const dateKeys = resolveDateKeys(departureAt);
          const dateKey = pickPrimaryDateKey(departureAt);
          if (!DATE_KEY_RE.test(dateKey)) return [] as TacticalMission[];
          const total = Number(inv?.totalSeats || 0);
          const avail = Number(inv?.availableSeats || 0);
          const held = Number(inv?.heldSeats || 0);
          const sold = Math.max(0, total - avail - held);
          if (sold <= 0) return [] as TacticalMission[];

          const bookingId = `SINV-SOLD-${invId || `${dateKey}-${inv?.fromCode || ''}-${inv?.toCode || ''}`}`;
          if (!(localBookingIdsByDate[dateKey]?.has(bookingId))) {
            if (!externalBookingIdsByDate[dateKey]) externalBookingIdsByDate[dateKey] = new Set<string>();
            externalBookingIdsByDate[dateKey].add(bookingId);
          }
          if (!dateKeys.includes(selectedDateKey)) return [] as TacticalMission[];
          const from = String(inv?.fromCode || '').toUpperCase();
          const to = String(inv?.toCode || '').toUpperCase();
          const normalizedInvTail = normalizeTailKey(String(inv?.metadata?.tailNumber || inv?.metadata?.aircraftTail || ''));
          const fallbackTail = normalizedInvTail && aircraftTailSet.has(normalizedInvTail) ? normalizedInvTail : 'UNASSIGNED';
          const targetTail = scheduledAssignments[bookingId] || fallbackTail;
          return [{
            id: `SINV-${invId}-${bookingId}`,
            from,
            to,
            paxCount: sold,
            etd: isoToClock(departureAt),
            eta: '',
            date: selectedDateKey,
            customerName: String(inv?.title || 'Scheduled Sold Seats'),
            quoteId: `SINV-${invId || dateKey}`,
            tailNumber: targetTail,
            productMode: 'SCHEDULED' as ProductMode,
            legIndex: 0,
            source: 'SCHEDULED' as const,
            bookingId,
            assignedCrew: scheduledCrewAssignments[bookingId] || [],
          } as TacticalMission];
        });

        const externalCounts = Object.entries(externalBookingIdsByDate).reduce((acc, [dateKey, ids]) => {
          acc[dateKey] = ids.size;
          return acc;
        }, {} as Record<string, number>);
        setScheduledPlanExternalCountByDate(externalCounts);
        setScheduledPlanMissions([...planMissions, ...inventoryFallbackMissions]);
      } catch (error) {
        console.error('[PlanningBoard] Failed to load scheduled plan tickets:', error);
        if (!cancelled) {
          setScheduledPlanMissions([]);
          setScheduledPlanExternalCountByDate({});
        }
      }
    };
    loadScheduledPlanTickets();
    return () => {
      cancelled = true;
    };
  }, [selectedDate, aircraftTailSet, scheduledAssignments, scheduledCrewAssignments, bookings]);

  useEffect(() => {
    const fetchCrewDuties = async () => {
      if (!isDFO || crewCandidates.length === 0) {
        setCrewDutyMap({});
        return;
      }
      setCrewLoading(true);
      try {
        const frmsPilots = await frmsService.getAllPilots();
        const frmsByEmail: Record<string, any> = {};
        frmsPilots.forEach(p => {
          if (p.email) frmsByEmail[p.email.toLowerCase()] = p;
        });
        const dateKey = normalizeDateKey(selectedDate);
        const results = await Promise.all(
          crewCandidates.map(async pilot => {
            const pilotEmail = (pilot as any).email || '';
            const frmsPilot = pilotEmail ? frmsByEmail[pilotEmail.toLowerCase()] : null;
            const frmsPilotId = frmsPilot?.id || pilot.id;
            const duties = await frmsService.getPilotDuties(frmsPilotId, dateKey, dateKey);
            const hasStandby = duties.some(d => d.type === 'STANDBY');
            const hasDuty = duties.some(d => d.type !== 'OFF');
            const status = hasStandby ? 'STANDBY' : hasDuty ? 'ON_DUTY' : 'OFF';
            return { pilotId: pilot.id, status, duties };
          })
        );
        const nextMap: Record<string, { status: 'ON_DUTY' | 'STANDBY' | 'OFF'; duties: FRMSDuty[] }> = {};
        results.forEach(r => {
          nextMap[r.pilotId] = { status: r.status, duties: r.duties };
        });
        setCrewDutyMap(nextMap);
      } catch (error) {
        console.error('[PlanningBoard] Failed to fetch crew duties:', error);
        setCrewDutyMap({});
      } finally {
        setCrewLoading(false);
      }
    };
    fetchCrewDuties();
  }, [isDFO, crewCandidates, selectedDate]);

  const dutyFilteredCrew = useMemo(() => {
    if (!isDFO) return crewCandidates;
    return crewCandidates.filter(p => {
      const duty = crewDutyMap[p.id];
      return duty && (duty.status === 'ON_DUTY' || duty.status === 'STANDBY');
    });
  }, [crewCandidates, crewDutyMap, isDFO]);

  const getCrewRoleCode = (userId: string): string => {
    const crew = crewCandidates.find(c => c.id === userId);
    return String((crew as any)?.role || '').toUpperCase();
  };

  const validateCrewComposition = (crewIds: string[]): { valid: boolean; reason?: string } => {
    if (!crewIds || crewIds.length === 0) return { valid: false, reason: 'No crew assigned' };
    if (crewIds.length > 2) return { valid: false, reason: 'Maximum 2 crew allowed per leg' };
    const roles = crewIds.map(getCrewRoleCode);
    const pilotCount = roles.filter(r => r === 'PILOT' || r === 'CP' || r === 'DFO').length;
    const firstOfficerCount = roles.filter(r => r === 'FIRST_OFFICER').length;
    const unknownCount = roles.filter(r => r !== 'PILOT' && r !== 'CP' && r !== 'DFO' && r !== 'FIRST_OFFICER').length;
    if (unknownCount > 0) return { valid: false, reason: 'Only Pilot, Chief Pilot, DFO, or First Officer can be assigned' };
    if (pilotCount < 1) return { valid: false, reason: 'At least one Pilot/Chief Pilot is required' };
    if (firstOfficerCount > 1) return { valid: false, reason: 'Only one First Officer can be assigned per leg' };
    return { valid: true };
  };

  const opsSummary = useMemo(() => {
    const selectedDateKey = normalizeDateKey(selectedDate);
    const todayLegs = quotes
      .filter(q => q.status === 'APPROVED')
      .flatMap(q =>
        q.legs.filter(leg => resolveLegDateKey(q.date, { etd: leg.etd }) === selectedDateKey)
      );
    const todayBookings = bookings.filter(b => normalizeDateKey(b.date) === selectedDateKey);
    return {
      today: {
        pax: todayLegs.reduce((sum, l) => sum + (l.paxCount || 0), 0) + todayBookings.reduce((sum, b) => sum + (b.paxCount || 0), 0),
        sectors: todayLegs.length + todayBookings.length,
        fuelEstimate: (todayLegs.length + todayBookings.length) * 120,
      },
    };
  }, [quotes, bookings, selectedDate]);

  const missions: TacticalMission[] = useMemo(() => {
    const selectedDateKey = normalizeDateKey(selectedDate);
    const list: TacticalMission[] = [];
    quotes
      .filter(q => q.status !== 'REJECTED' && q.status !== 'DRAFT')
      .forEach(q => {
        const normalizedQuoteTail = normalizeTailKey(q.tailNumber || '');
        const quoteSource: 'CHARTER' | 'SCHEDULED' = q.productMode === 'SCHEDULED' ? 'SCHEDULED' : 'CHARTER';
        const intakeMeta = ((q.costs || {}) as any)?.intake || {};
        const linkedInbound = inboundQueueByQuoteId[q.id];
        const inboundHub =
          String(intakeMeta?.source || '').toUpperCase() === 'EMAIL_INTAKE' || linkedInbound
            ? {
                source: linkedInbound?.source || intakeMeta?.source || null,
                inboxKey: linkedInbound?.inboxKey || intakeMeta?.inboxKey || null,
                subject: linkedInbound?.subject || intakeMeta?.inboundSubject || null,
                messageId: linkedInbound?.messageId || intakeMeta?.inboundMessageId || null,
                threadId: linkedInbound?.threadId || intakeMeta?.inboundThreadId || null,
                verificationStatus: linkedInbound?.verificationStatus || intakeMeta?.verificationStatus || null,
                caseState: linkedInbound?.caseState || null,
                repliedAt: linkedInbound?.repliedAt || null,
              }
            : null;
        q.legs.forEach((leg, idx) => {
          if (resolveLegDateKey(q.date, { etd: leg.etd }) !== selectedDateKey) return;
          const scheduledKey = String((leg as any)?.bookingId || q.bookingId || leg.id || `${q.id}-${idx}`);
          const resolvedTailNumber =
            quoteSource === 'SCHEDULED'
              ? scheduledAssignments[scheduledKey] || (normalizedQuoteTail && aircraftTailSet.has(normalizedQuoteTail) ? normalizedQuoteTail : 'UNASSIGNED')
              : normalizedQuoteTail && aircraftTailSet.has(normalizedQuoteTail)
              ? normalizedQuoteTail
              : 'UNASSIGNED';
          const resolvedCrew =
            quoteSource === 'SCHEDULED'
              ? scheduledCrewAssignments[scheduledKey] || leg.assignedCrew || []
              : leg.assignedCrew || [];
          list.push({
            ...leg,
            date: q.date,
            customerName: q.customerName,
            quoteId: q.id,
            tailNumber: resolvedTailNumber,
            productMode: q.productMode as ProductMode,
            specialInstructions: q.specialInstructions,
            legIndex: idx,
            source: quoteSource,
            bookingId: scheduledKey,
            assignedCrew: resolvedCrew,
            inboundHub,
          });
        });
      });
    const bookingMissionIds = new Set<string>();
    bookings.filter(b => normalizeDateKey(b.date) === selectedDateKey).forEach((b, idx) => {
      bookingMissionIds.add(String(b.id));
      list.push({
        id: b.id,
        from: b.from,
        to: b.to,
        paxCount: b.paxCount,
        etd: '',
        eta: '',
        date: b.date,
        customerName: b.customerName,
        quoteId: `BOOKING-${b.id}`,
        tailNumber: scheduledAssignments[b.id] || 'UNASSIGNED',
        productMode: 'SCHEDULED',
        legIndex: idx,
        source: 'SCHEDULED',
        bookingId: b.id,
        assignedCrew: scheduledCrewAssignments[b.id] || [],
      } as TacticalMission);
    });
    scheduledPlanMissions.forEach((m) => {
      const key = String(m.bookingId || '');
      if (key && bookingMissionIds.has(key)) return;
      list.push(m);
    });
    return list;
  }, [quotes, bookings, selectedDate, scheduledAssignments, scheduledCrewAssignments, aircraftTailSet, scheduledPlanMissions, inboundQueueByQuoteId]);

  const charterReservoir = missions.filter(m => m.source === 'CHARTER' && m.tailNumber === 'UNASSIGNED');
  const scheduledReservoir = missions.filter(m => m.source === 'SCHEDULED' && m.tailNumber === 'UNASSIGNED');
  const charterUnassignedCount = charterReservoir.length;
  const scheduledUnassignedCount = scheduledReservoir.length;

  const charterCrewInvalidCount = useMemo(() => {
    const selectedDateKey = normalizeDateKey(selectedDate);
    const dayCharterQuotes = quotes.filter(q => q.status !== 'REJECTED' && q.status !== 'DRAFT');
    return dayCharterQuotes.reduce(
      (count, q) =>
        count +
        q.legs.filter(
          leg =>
            resolveLegDateKey(q.date, { etd: leg.etd }) === selectedDateKey &&
            !validateCrewComposition(leg.assignedCrew || []).valid
        ).length,
      0
    );
  }, [quotes, selectedDate, crewCandidates]);

  const advisories = useMemo(() => {
    if (!settings.agent?.enabled || !settings.agent.modules?.PLANNER) return [];
    if (!settings.agent.rolesByModule?.PLANNER?.includes(currentRole)) return [];
    const selectedDateKey = normalizeDateKey(selectedDate);
    const dayQuotes = quotes.filter(q =>
      q.legs.some(leg => resolveLegDateKey(q.date, { etd: leg.etd }) === selectedDateKey)
    );
    const raw = getPlannerAdvisories(dayQuotes);
    const custom = evaluateCustomRules(settings.agent?.customRulesByModule?.PLANNER, { quotes: dayQuotes });
    return filterAdvisories([...raw, ...custom], settings, 'PLANNER');
  }, [quotes, selectedDate, settings, currentRole]);

  useEffect(() => {
    try {
      const selectedDateKey = normalizeDateKey(selectedDate);
      localStorage.setItem(
        'ma_agent_context_PLANNER',
        JSON.stringify({
          quotes: quotes.filter(q =>
            q.legs.some(leg => resolveLegDateKey(q.date, { etd: leg.etd }) === selectedDateKey)
          ),
        })
      );
    } catch {
      // ignore
    }
  }, [quotes, selectedDate]);

  const enforcement = getEnforcementPolicy(settings, 'PLANNER');
  const finalAuditFail =
    settings.agent?.enabled &&
    settings.agent.modules?.PLANNER &&
    advisories.some(a => enforcement.blockSeverities.includes(a.severity));

  const acknowledgeAdvisories = async () => {
    if (!advisories.length) return;
    await agentService.logAudit('AGENT_ADVISORY_ACK', { module: 'PLANNER', count: advisories.length, advisories });
  };

  const fleetSchedule = useMemo(() => {
    const groups: Record<string, TacticalMission[]> = {};
    aircrafts.forEach(ac => {
      groups[ac.tailNumber] = missions.filter(m => m.tailNumber === ac.tailNumber).sort((a, b) => (a.etd || '00:00').localeCompare(b.etd || '00:00'));
    });
    return groups;
  }, [missions, aircrafts]);

  const buildRouteSectorsForTail = (tailNumber: string): PlannerRouteSectorInput[] => {
    const tail = normalizeTailKey(tailNumber);
    if (!tail) return [];
    const scheduledForTail = (fleetSchedule[tail] || []).filter((m) => m.source === 'SCHEDULED');
    return scheduledForTail
      .map((m, idx) => ({
        sectorKey: String(m.bookingId || m.id || `${m.quoteId}:${m.legIndex ?? idx}`),
        tailNumber: tail,
        from: String(m.from || '').toUpperCase(),
        to: String(m.to || '').toUpperCase(),
        pax: Math.max(0, Number(m.paxCount || 0)),
        etd: String(m.etd || '').trim() || undefined,
        eta: String(m.eta || '').trim() || undefined,
        bookingId: m.bookingId ? String(m.bookingId) : undefined,
        missionId: m.id ? String(m.id) : undefined,
        isSynthetic: isSyntheticScheduledMission(m),
      }))
      .filter((s) => !!s.from && !!s.to);
  };

  const reloadPlannerRoutesForSelectedDate = async () => {
    const dateKey = normalizeDateKey(selectedDate);
    if (!DATE_KEY_RE.test(dateKey)) return;
    const routes = await plannerService.getPlannerRoutes(dateKey);
    const byTail = (Array.isArray(routes) ? routes : []).reduce((acc, route) => {
      const tail = normalizeTailKey(route?.tailNumber || '');
      if (!tail) return acc;
      acc[tail] = route;
      return acc;
    }, {} as Record<string, PlannerRoute>);
    setPlannerRoutesByTail(byTail);
    setPlannerRoutesLoadedDate(dateKey);
  };

  const handleSaveGeneratedRouteForTail = async (tailNumber: string) => {
    const dateKey = normalizeDateKey(selectedDate);
    const tail = normalizeTailKey(tailNumber);
    if (!DATE_KEY_RE.test(dateKey) || !tail) return;
    const sectors = buildRouteSectorsForTail(tail);
    if (sectors.length === 0) {
      setAssignmentToast('Route not saved');
      return;
    }
    setRouteSavingTail(tail);
    try {
      const result = await plannerService.generatePlannerRoute({
        date: dateKey,
        tailNumber: tail,
        sectors,
      });
      await reloadPlannerRoutesForSelectedDate();
      const skip = (result?.skipped || []).find((s) => normalizeTailKey(s.tailNumber) === tail);
      if (skip) {
        setAssignmentToast(skip.reason || 'Route not saved');
      } else {
        setAssignmentToast('Route saved to planner');
      }
    } catch {
      setAssignmentToast('Route not saved');
    } finally {
      setRouteSavingTail(null);
    }
  };

  const handleSaveManualRouteEdits = async (
    routeId: string,
    payload: {
      stops: PlannerRouteStopUpdateInput[];
      warnings?: any;
      diagnostics?: any;
      summaryPath?: string;
      maxOnboardPax?: number;
    }
  ): Promise<boolean> => {
    if (!routeId) return false;
    try {
      await plannerService.updatePlannerRoute(routeId, payload);
      await reloadPlannerRoutesForSelectedDate();
      setAssignmentToast('Manual route edits saved');
      return true;
    } catch {
      setAssignmentToast('Manual route edits not saved');
      return false;
    }
  };

  const handleLockRoute = async (routeId: string): Promise<boolean> => {
    if (!routeId) return false;
    try {
      await plannerService.lockPlannerRoute(routeId);
      await reloadPlannerRoutesForSelectedDate();
      setAssignmentToast('Route locked');
      return true;
    } catch {
      setAssignmentToast('Action failed');
      return false;
    }
  };

  const handleConfirmRoute = async (routeId: string): Promise<boolean> => {
    if (!routeId) return false;
    try {
      await plannerService.confirmPlannerRoute(routeId);
      await reloadPlannerRoutesForSelectedDate();
      setAssignmentToast('Route confirmed');
      return true;
    } catch {
      setAssignmentToast('Action failed');
      return false;
    }
  };

  const handleResetRoute = async (routeId: string): Promise<boolean> => {
    if (!routeId) return false;
    try {
      await plannerService.resetPlannerRoute(routeId);
      await reloadPlannerRoutesForSelectedDate();
      setAssignmentToast('Route reset');
      return true;
    } catch {
      setAssignmentToast('Action failed');
      return false;
    }
  };

  const scheduledPlanExternalCountForSelectedDate = useMemo(() => {
    const selectedDateKey = normalizeDateKey(selectedDate);
    return scheduledPlanExternalCountByDate[selectedDateKey] || 0;
  }, [selectedDate, scheduledPlanExternalCountByDate]);

  const historyRows: PlanningHistoryRow[] = useMemo(
    () => buildPlanningHistoryRows(quotes, bookings, scheduledAssignments, validateCrewComposition),
    [quotes, bookings, scheduledAssignments, validateCrewComposition]
  );

  const handleDragMissionStart = (source: 'CHARTER' | 'SCHEDULED', qId: string, lId: string) => {
    if (!isFlightPlanner || opStatus === 'FINAL_CLOSE') return;
    setDraggedMissionId(`${source}|${qId}|${lId}`);
    setDraggedPilotId(null);
  };

  const handleDragPilotStart = (id: string) => {
    if (!isDFO) return;
    setDraggedPilotId(id);
    setDraggedMissionId(null);
  };

  const handleDropOnAircraft = (targetTail: string) => {
    if (finalAuditFail && !settings.agent?.advisoryOnly) {
      alert('Final Audit failed: resolve blocking advisories before dispatch assignment.');
      agentService.logAudit('AGENT_FINAL_AUDIT_FAIL', { module: 'PLANNER', advisories });
      return;
    }
    if (!draggedMissionId) return;
    const [source, qId, legId] = draggedMissionId.split('|');
    if (source === 'CHARTER' && onUpdateQuote) {
      onUpdateQuote(qId, { tailNumber: targetTail });
      setAssignmentToast(`Saved to ${targetTail}`);
    }
    if (source === 'SCHEDULED') {
      setScheduledPersistenceStatus('idle');
      setAssignmentToast(null);
      setScheduledAssignments(prev => ({ ...prev, [legId]: targetTail }));
    }
    setDraggedMissionId(null);
  };

  const handleAssignMissionToAircraft = (
    source: 'CHARTER' | 'SCHEDULED',
    quoteId: string,
    legId: string,
    targetTail: string
  ) => {
    if (finalAuditFail && !settings.agent?.advisoryOnly) {
      alert('Final Audit failed: resolve blocking advisories before dispatch assignment.');
      agentService.logAudit('AGENT_FINAL_AUDIT_FAIL', { module: 'PLANNER', advisories });
      return;
    }
    if (source === 'CHARTER' && onUpdateQuote) {
      onUpdateQuote(quoteId, { tailNumber: targetTail });
      setAssignmentToast(`Saved to ${targetTail}`);
    }
    if (source === 'SCHEDULED') {
      setScheduledPersistenceStatus('idle');
      setAssignmentToast(null);
      setScheduledAssignments(prev => ({ ...prev, [legId]: targetTail }));
    }
  };

  const handleDropOnReservoir = () => {
    if (!draggedMissionId) return;
    const [source, qId, legId] = draggedMissionId.split('|');
    if (source === 'CHARTER' && onUpdateQuote) {
      onUpdateQuote(qId, { tailNumber: 'UNASSIGNED' });
      setAssignmentToast('Moved back to queue');
    }
    if (source === 'SCHEDULED') {
      setScheduledPersistenceStatus('idle');
      setAssignmentToast(null);
      setScheduledAssignments(prev => ({ ...prev, [legId]: 'UNASSIGNED' }));
    }
    setDraggedMissionId(null);
  };

  useEffect(() => {
    if (!assignmentToast) return;
    const timer = window.setTimeout(() => setAssignmentToast(null), 1800);
    return () => window.clearTimeout(timer);
  }, [assignmentToast]);

  const handleDropPilotOnLeg = (quoteId: string, legId: string) => {
    if (finalAuditFail && !settings.agent?.advisoryOnly) {
      alert('Final Audit failed: resolve blocking advisories before crew assignment.');
      agentService.logAudit('AGENT_FINAL_AUDIT_FAIL', { module: 'PLANNER', advisories });
      return;
    }
    if (!draggedPilotId) return;
    const quote = quotes.find(q => q.id === quoteId);
    const upsertCrewLegError = (value?: string) => {
      setCrewLegErrors(prev => {
        const next = { ...prev };
        if (value) next[`${quoteId}:${legId}`] = value;
        else delete next[`${quoteId}:${legId}`];
        return next;
      });
    };
    const computeNextCrew = (currentCrew: string[]) => {
      const nextCrew = currentCrew.includes(draggedPilotId)
        ? currentCrew.filter(id => id !== draggedPilotId)
        : [...currentCrew, draggedPilotId];
      if (nextCrew.length > 2) return { blocked: true, reason: 'Only 2 crew members allowed per leg.', nextCrew: currentCrew };
      const validation = validateCrewComposition(nextCrew);
      if (!validation.valid && !currentCrew.includes(draggedPilotId)) {
        const reason = validation.reason || 'Invalid crew composition.';
        const hardBlock = reason !== 'At least one Pilot/Chief Pilot is required';
        return { blocked: hardBlock, reason, nextCrew: hardBlock ? currentCrew : nextCrew };
      }
      return { blocked: false, reason: '', nextCrew };
    };

    if (quote && onUpdateQuote) {
      const updatedLegs = quote.legs.map(l => {
        if (l.id !== legId) return l;
        const currentCrew = l.assignedCrew || [];
        const result = computeNextCrew(currentCrew);
        if (result.blocked) return l;
        if (result.reason) upsertCrewLegError(result.reason);
        else upsertCrewLegError();
        return { ...l, assignedCrew: result.nextCrew };
      });
      onUpdateQuote(quoteId, { legs: updatedLegs });
      setDraggedPilotId(null);
      return;
    }

    const mission = missions.find(m => m.quoteId === quoteId && m.id === legId);
    const scheduledKey = String(mission?.bookingId || legId || '');
    if (!scheduledKey) {
      upsertCrewLegError('Unable to resolve scheduled leg assignment key.');
      setDraggedPilotId(null);
      return;
    }
    const signature = getCrewAssignmentGroupSignature(mission);
    const groupedKeys = Array.from(
      new Set(
        missions
          .filter(m => m.source === 'SCHEDULED' && getCrewAssignmentGroupSignature(m) === signature)
          .map(m => String(m.bookingId || m.id || ''))
          .filter(Boolean)
      )
    );
    const targetKeys = groupedKeys.length > 0 ? groupedKeys : [scheduledKey];
    setScheduledCrewAssignments(prev => {
      const currentCrew = prev[scheduledKey] || mission?.assignedCrew || [];
      const result = computeNextCrew(currentCrew);
      if (result.blocked) {
        upsertCrewLegError(result.reason);
        return prev;
      }
      if (result.reason) upsertCrewLegError(result.reason);
      else upsertCrewLegError();
      const next = { ...prev };
      targetKeys.forEach((key) => {
        next[key] = result.nextCrew;
      });
      setScheduledPersistenceStatus('idle');
      setAssignmentToast(null);
      return next;
    });
    setDraggedPilotId(null);
  };

  const handleUnassignPilot = (pilotId: string, quoteId: string, legId: string) => {
    if (!isDFO) return;
    const quote = quotes.find(q => q.id === quoteId);
    if (quote && onUpdateQuote) {
      const updatedLegs = quote.legs.map(l => (l.id === legId ? { ...l, assignedCrew: l.assignedCrew?.filter(id => id !== pilotId) } : l));
      onUpdateQuote(quoteId, { legs: updatedLegs });
      return;
    }
    const mission = missions.find(m => m.quoteId === quoteId && m.id === legId);
    const scheduledKey = String(mission?.bookingId || legId || '');
    if (!scheduledKey) return;
    const signature = getCrewAssignmentGroupSignature(mission);
    const groupedKeys = Array.from(
      new Set(
        missions
          .filter(m => m.source === 'SCHEDULED' && getCrewAssignmentGroupSignature(m) === signature)
          .map(m => String(m.bookingId || m.id || ''))
          .filter(Boolean)
      )
    );
    const targetKeys = groupedKeys.length > 0 ? groupedKeys : [scheduledKey];
    setScheduledCrewAssignments(prev => ({
      ...prev,
      ...targetKeys.reduce((acc, key) => {
        acc[key] = (prev[key] || []).filter(id => id !== pilotId);
        return acc;
      }, {} as Record<string, string[]>),
    }));
    setScheduledPersistenceStatus('idle');
    setAssignmentToast(null);
  };

  const handleFuelCommit = () => {
    if (!showFuelModal || !onUpdateAircraft) return;
    const ac = aircrafts.find(a => a.tailNumber === showFuelModal);
    if (!ac) return;
    const half = Math.round(fuelLiters / 2);
    onUpdateAircraft(ac.id, {
      fuelStatus: {
        leftWing: (ac.fuelStatus?.leftWing || 0) + half,
        rightWing: (ac.fuelStatus?.rightWing || 0) + half,
        lastUpdated: new Date().toISOString(),
      },
    });
    setShowFuelModal(null);
    setFuelLiters(0);
  };

  return {
    selectedDate,
    setSelectedDate,
    viewMode,
    setViewMode,
    opStatus,
    advisories,
    acknowledgeAdvisories,
    opsSummary,
    charterConfirmed,
    setCharterConfirmed,
    scheduledConfirmed,
    setScheduledConfirmed,
    charterUnassignedCount,
    charterCrewInvalidCount,
    scheduledUnassignedCount,
    historyRows,
    isDFO,
    isFlightPlanner,
    draggedMissionId,
    draggedPilotId,
    crewLoading,
    dutyFilteredCrew,
    crewDutyMap,
    charterReservoir,
    scheduledReservoir,
    missions,
    fleetSchedule,
    scheduledPlanExternalCountForSelectedDate,
    scheduledPlanExternalCountByDate,
    plannerRoutesByTail,
    plannerRoutesLoadedDate,
    routeSavingTail,
    assignmentToast,
    scheduledPersistenceStatus,
    pilots,
    crewLegErrors,
    showFuelModal,
    setShowFuelModal,
    fuelLiters,
    setFuelLiters,
    handleDragMissionStart,
    handleDragPilotStart,
    handleDropOnReservoir,
    handleDropOnAircraft,
    handleAssignMissionToAircraft,
    handleDropPilotOnLeg,
    handleUnassignPilot,
    handleFuelCommit,
    handleSaveGeneratedRouteForTail,
    handleSaveManualRouteEdits,
    handleLockRoute,
    handleConfirmRoute,
    handleResetRoute,
    getCrewRoleCode,
    setScheduledAssignments,
    ...manifest,
  };
}
